/**
 * Download offers — what the agent puts in the chat when someone asks for
 * their data as a file.
 *
 * The offer is a pointer, not a payload: it names a DuckDB frame, and the
 * bytes stream from `/frame-data/{frameId}/download` under the viewer's own
 * token when they click. Two consequences the card has to live with — the
 * file never exists until someone asks for it, and a frame from an old
 * session can be gone by the time they do.
 */

export type DownloadFormat = 'xlsx' | 'csv';

/** One of the other tables in this thread, offered when we may have guessed. */
export interface DownloadAlternative {
  frameId: string;
  title: string;
  rowCount: number;
  /** Titles repeat across a thread; the shape is what tells them apart. */
  columnCount: number;
}

export interface DownloadOffer {
  /** Stable per offer, so the same card survives a reload. */
  id: string;
  frameId: string;
  title: string;
  rowCount: number;
  columnCount: number;
  /** The format the analyst named, if they named one. Both are always offered. */
  format: DownloadFormat;
  /** Server-side export ceiling; only worth showing when rowCount exceeds it. */
  maxRows?: number | null;
  alternatives: DownloadAlternative[];
}

/** Map a `download_ready` SSE payload / history row onto the client shape. */
export function toDownloadOffer(raw: any, fallbackId?: string): DownloadOffer {
  const frameId = String(raw?.frame_id || raw?.frameId || '');
  return {
    id: String(raw?.id || fallbackId || `dl-${frameId}`),
    frameId,
    title: String(raw?.title || 'Results'),
    rowCount: Number(raw?.row_count ?? raw?.rowCount ?? 0),
    columnCount: Number(raw?.column_count ?? raw?.columnCount ?? 0),
    format: raw?.format === 'csv' ? 'csv' : 'xlsx',
    maxRows: raw?.max_rows ?? raw?.maxRows ?? null,
    alternatives: (raw?.alternatives || []).map((a: any) => ({
      frameId: String(a?.frame_id || a?.frameId || ''),
      title: String(a?.title || 'Results'),
      rowCount: Number(a?.row_count ?? a?.rowCount ?? 0),
      columnCount: Number(a?.column_count ?? a?.columnCount ?? 0),
    })).filter((a: DownloadAlternative) => a.frameId),
  };
}
